import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Avatar, Divider, Button,
} from '@mui/material';
import {
  BusinessOutlined, AdminPanelSettingsOutlined, PeopleAltOutlined,
  LogoutOutlined, AdminPanelSettings,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

import OrgsTab  from './superadmin/OrgsTab';
import RolesTab from './superadmin/RolesTab';
import UsersTab from './superadmin/UsersTab';

const PRIMARY = '#1075BA';
const LOGO_URL = 'https://res.cloudinary.com/dasne3x79/image/upload/v1774480405/logo.png';

const TABS = [
  { label: 'Companies', icon: BusinessOutlined },
  { label: 'Roles',         icon: AdminPanelSettingsOutlined },
  { label: 'Users',         icon: PeopleAltOutlined },
];

// ── Account popup ─────────────────────────────────────────────────────────────
function AccountPopup({ user, onLogout, anchorRef }) {
  const popupRef = useRef();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) {
        // tell parent to close — handled via the toggle in parent
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorRef]);

  return (
    <Box
      ref={popupRef}
      sx={{
        position: 'absolute',
        top: 'calc(100% + 10px)',
        right: 0,
        width: 280,
        background: '#fff',
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
        zIndex: 200,
        overflow: 'hidden',
        animation: 'popIn 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)',
        '@keyframes popIn': {
          from: { opacity: 0, transform: 'scale(0.94) translateY(-6px)' },
          to:   { opacity: 1, transform: 'scale(1) translateY(0)' },
        },
        transformOrigin: 'top right',
      }}
    >
      {/* Header — avatar + name */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.75 }}>
        <Avatar sx={{
          width: 46, height: 46, flexShrink: 0,
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a8fd6 100%)`,
          fontSize: '1rem', fontWeight: 800,
          fontFamily: '"Nunito", sans-serif',
          border: `2px solid ${alpha(PRIMARY, 0.2)}`,
        }}>
          {user?.firstName?.[0]}{user?.lastName?.[0]}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 800, fontSize: '0.9rem', color: '#1a1a2e',
            fontFamily: '"Nunito", sans-serif',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography sx={{
            fontSize: '0.75rem', color: '#5f6368',
            fontFamily: '"Nunito", sans-serif',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {user?.email}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(0,0,0,0.07)' }} />

      {/* Role + org info */}
      <Box sx={{ px: 2.5, py: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <AdminPanelSettings sx={{ fontSize: 15, color: PRIMARY }} />
          <Typography sx={{ fontSize: '0.78rem', color: '#3c4043', fontFamily: '"Nunito", sans-serif', fontWeight: 600 }}>
            {user?.role?.name || 'Super Admin'}
          </Typography>
        </Box>
        {user?.organization?.name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessOutlined sx={{ fontSize: 15, color: '#5f6368' }} />
            <Typography sx={{ fontSize: '0.78rem', color: '#5f6368', fontFamily: '"Nunito", sans-serif' }}>
              {user.organization.name}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(0,0,0,0.07)' }} />

      {/* Logout */}
      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          onClick={onLogout}
          startIcon={<LogoutOutlined sx={{ fontSize: 17 }} />}
          sx={{
            justifyContent: 'flex-start',
            color: '#d32f2f',
            fontFamily: '"Nunito", sans-serif',
            fontWeight: 600,
            fontSize: '0.83rem',
            borderRadius: 2,
            px: 1.5, py: 1,
            textTransform: 'none',
            '&:hover': { background: alpha('#d32f2f', 0.06) },
          }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────
export default function SuperAdminDashboard() {
  const [tab, setTab]           = useState(0);
  const [popupOpen, setPopup]   = useState(false);
  const { user, logout }        = useAuth();
  const navigate                = useNavigate();
  const avatarRef               = useRef();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Close popup on outside click
  useEffect(() => {
    if (!popupOpen) return;
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setPopup(false);
      }
    };
    // slight delay so the avatar click that opened it doesn't immediately close it
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 10);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [popupOpen]);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Nunito", sans-serif',
    }}>

      {/* ── Top app bar ───────────────────────────────────────────────────── */}
      <Box sx={{
        background: '#fff',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        {/* Upper row */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, md: 4 }, pt: 1.5, pb: 0,
        }}>

          {/* Logo: image + "Events" in black */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={LOGO_URL}
              alt="GoVirtual logo"
              sx={{ height: 32, width: 'auto', display: 'block', objectFit: 'contain' }}
            />
            <Typography sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#1a1a2e',
              fontFamily: '"Nunito", sans-serif',
              lineHeight: 1,
              letterSpacing: '-0.01em',
            }}>
              Events
            </Typography>
          </Box>

          {/* Right: avatar only */}
          <Box ref={avatarRef} sx={{ position: 'relative' }}>
            <Avatar
              onClick={() => setPopup(v => !v)}
              sx={{
                width: 34, height: 34,
                cursor: 'pointer',
                background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a8fd6 100%)`,
                fontSize: '0.8rem', fontWeight: 800,
                fontFamily: '"Nunito", sans-serif',
                border: `2px solid ${popupOpen ? PRIMARY : alpha(PRIMARY, 0.2)}`,
                boxShadow: popupOpen ? `0 0 0 3px ${alpha(PRIMARY, 0.15)}` : 'none',
                transition: 'all 0.15s',
                '&:hover': {
                  border: `2px solid ${PRIMARY}`,
                  boxShadow: `0 0 0 3px ${alpha(PRIMARY, 0.12)}`,
                },
              }}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>

            {popupOpen && (
              <AccountPopup
                user={user}
                onLogout={handleLogout}
                anchorRef={avatarRef}
              />
            )}
          </Box>
        </Box>

        {/* Lower row: centered tabs */}
        <Box sx={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          mt: 0.5, gap: 0,
        }}>
          {TABS.map((t, i) => {
            const active = tab === i;
            const Icon = t.icon;
            return (
              <Box
                key={t.label}
                onClick={() => setTab(i)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.75, py: 1.1,
                  cursor: 'pointer',
                  borderBottom: active ? `2.5px solid ${PRIMARY}` : '2.5px solid transparent',
                  color: active ? PRIMARY : '#5f6368',
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: active ? 700 : 500,
                  fontSize: '0.875rem',
                  transition: 'color 0.15s',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: active ? PRIMARY : '#3c4043',
                    background: alpha(PRIMARY, 0.04),
                    borderRadius: '6px 6px 0 0',
                  },
                }}
              >
                <Icon sx={{ fontSize: 17, opacity: active ? 1 : 0.7 }} />
                <Typography sx={{
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: 'inherit',
                  fontSize: 'inherit',
                  color: 'inherit',
                  lineHeight: 1,
                }}>
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Page content ──────────────────────────────────────────────────── */}
      <Box sx={{
        flex: 1,
        px: { xs: 2, md: 4 },
        py: 3.5,
        maxWidth: 1000,
        width: '100%',
        mx: 'auto',
        boxSizing: 'border-box',
      }}>
        <Typography sx={{
          fontWeight: 700, fontSize: '1.25rem', color: '#1a1a2e',
          mb: 3, fontFamily: '"Nunito", sans-serif',
        }}>
          {TABS[tab].label}
        </Typography>

        {tab === 0 && <OrgsTab hideTitle />}
        {tab === 1 && <RolesTab hideTitle />}
        {tab === 2 && <UsersTab hideTitle />}
      </Box>
    </Box>
  );
}